import { theme, Tooltip } from "antd";
import {
  CSSProperties,
  PropsWithChildren,
  createContext,
  useContext,
  ReactNode,
  useState,
  useCallback,
} from "react";
import { CaretLeftFilled, CaretRightFilled } from "@ant-design/icons";
import { Field, SearchActions } from "src/search/types";
import { SearchContext } from "src/search/useSearchContext";
import React from "react";
import { TableColumnHeaderTooltip } from "pages/[orgId]/explore/lib/tooltips/VZBLTooltips";
import { TableFormatter } from "../utils/table-utils";
import "./Table.module.css";
import { ResizableColumn } from "./ResizableColumn";

// Define the context type
interface TableHeaderContextType {
  renderTooltip?: (title: ReactNode, children: ReactNode) => ReactNode;
}

// Create the context
const TableHeaderContext = createContext<TableHeaderContextType>({});

// Create the context with proper typing
interface ColumnWidthContextType {
  widths: { [key: string]: number };
  setWidth: (colIndex: string, width: number) => void;
}

const ColumnWidthContext = createContext<ColumnWidthContextType>({
  widths: {},
  setWidth: () => {},
});

// Create a provider component
export function TableHeaderProvider({
  children,
  renderTooltip,
}: PropsWithChildren<TableHeaderContextType>) {
  const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>(
    {}
  );

  const setColumnWidth = useCallback((colIndex: string, width: number) => {
    setColumnWidths((prev) => ({ ...prev, [colIndex]: width }));
  }, []);

  return (
    <ColumnWidthContext.Provider
      value={{
        widths: columnWidths,
        setWidth: setColumnWidth,
      }}
    >
      <TableHeaderContext.Provider value={{ renderTooltip }}>
        {children}
      </TableHeaderContext.Provider>
    </ColumnWidthContext.Provider>
  );
}

// Hook to use the tooltip renderer
function useTableHeaderTooltip() {
  const context = useContext(TableHeaderContext);
  return context.renderTooltip;
}

export function TableHeaderCustomComponent(
  props: PropsWithChildren<{
    style: CSSProperties;
    className: string;
    resizable?: boolean;
    sortable?: boolean;
    movable?: boolean;
    "data-col-index"?: string;
  }>
) {
  const { widths, setWidth } = useContext(ColumnWidthContext);
  const {
    resizable = true,
    sortable = true,
    movable = true,
    "data-col-index": colIndex,
    ...restProps
  } = props;

  const renderTooltip = useTableHeaderTooltip();

  const title =
    typeof props.children === "string"
      ? props.children.replace(/[^\s\w]/g, "")
      : undefined;

  // Get width from context or props
  const width = colIndex ? widths[colIndex] : null;
  const initialWidth = colIndex ? (props.style?.width as number) || 200 : null;
  const currentWidth =
    width || initialWidth || (props.style?.width as number) || 200;

  // Handle content rendering
  let content;
  if (Array.isArray(props.children)) {
    const [tooltipContent, textContent, ...sortCarets] = props.children;
    content = (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          gap: "8px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            minWidth: 0,
            width: "100%",
          }}
        >
          {renderTooltip && tooltipContent ? (
            renderTooltip(
              tooltipContent,
              <div className="trunc">{textContent}</div>
            )
          ) : (
            <div className="trunc">{textContent || props.children}</div>
          )}
        </div>
        {sortable && sortCarets && sortCarets.length > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {sortCarets}
          </div>
        )}
      </div>
    );
  } else {
    content = props.children;
  }

  const headerContent = (
    <div
      title={title}
      style={{
        whiteSpace: "nowrap",
        fontSize: 11,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.3px",
        color: "var(--color-text-secondary)",
        display: "flex",
        alignItems: "center",
        gap: "4px",
        width: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        position: "relative",
      }}
    >
      {content}
    </div>
  );

  if (resizable) {
    return (
      <ResizableColumn
        width={currentWidth}
        colIndex={colIndex}
        widths={widths}
        setWidth={setWidth}
        className={props.className}
        style={props.style}
      >
        {headerContent}
      </ResizableColumn>
    );
  }

  return (
    <th {...restProps} className={props.className}>
      {headerContent}
    </th>
  );
}

export function GroupedTableHeader(props: {
  field: Field;
  index: number;
  params: SearchContext["params"];
  title?: string;
  showMovers?: boolean;
}) {
  const { token } = theme.useToken();

  const title = TableFormatter.formatDimension(
    props.title,
    props.field.attribute
  );

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
      {props.showMovers &&
        props.index > 0 &&
        (props.params.state.groupBys?.length || 0) > 1 && (
          <CaretLeftFilled
            title="Move Left"
            style={{
              color: token.colorTextQuaternary,
              fontSize: "12px",
              cursor: "pointer",
            }}
            onClick={() => {
              const bys = props.params.state.groupBys || [];
              props.params.dispatch({
                type: SearchActions.setGroupBys,
                value: [
                  ...bys.slice(0, props.index - 1),
                  bys.at(props.index)!,
                  bys.at(props.index - 1)!,
                  ...bys.slice(props.index + 1),
                ],
              });
            }}
          />
        )}
      <TableColumnHeaderTooltip field={props.field} title={title} />

      {props.showMovers &&
        props.index < (props.params.state.groupBys?.length || 0) - 1 &&
        (props.params.state.groupBys?.length || 0) > 1 && (
          <CaretRightFilled
            title="Move Right"
            style={{
              color: token.colorTextQuaternary,
              fontSize: "12px",
              cursor: "pointer",
            }}
            onClick={() => {
              const bys = props.params.state.groupBys || [];
              props.params.dispatch({
                type: SearchActions.setGroupBys,
                value: [
                  ...bys.slice(0, props.index),
                  bys.at(props.index + 1)!,
                  bys.at(props.index)!,
                  ...bys.slice(props.index + 2),
                ],
              });
            }}
          />
        )}
    </div>
  );
}
